import { z } from 'zod';
import { Id, TimestampMs } from './common.js';
/**
 * Wire shapes for command signing (docs/signing.md). `@viko/crypto` owns canonicalization,
 * hashing and verification; these zod schemas mirror its `CommandEnvelope` / `SignedCommand`
 * types so frames can be validated at every hop. Additive-only like the rest of the protocol.
 */
export const COMMAND_ENVELOPE_VERSION = 1;
export const RiskLevel = z.enum(['normal', 'risky']);
/** The signed part of every command (JCS-canonicalized, signed under `viko/command/v1`). */
export const CommandEnvelope = z.object({
    v: z.literal(COMMAND_ENVELOPE_VERSION),
    /** UUID. Also the protocol idempotency key: the daemon executes each id at most once. */
    id: Id,
    /** Target daemon; a command for another machine is rejected. */
    machineId: Id,
    /** Command kind from the registry in `commands.ts` (e.g. `run.start`, `land.merge`). */
    kind: z.string().min(1),
    /** base64url SHA-256 over the domain-tagged JCS payload. */
    payloadHash: z.string().min(1),
    /** ≥128-bit random, base64url. */
    nonce: z.string().min(16),
    issuedAt: TimestampMs,
    expiresAt: TimestampMs,
    deviceKeyId: Id,
    /** Client's claim; the daemon's risky-kind policy can only raise it. */
    riskLevel: RiskLevel,
});
/** base64url WebAuthn assertion (passkey step-up / approvals). */
export const WebAuthnAssertion = z.object({
    credentialId: z.string(),
    authenticatorData: z.string(),
    clientDataJSON: z.string(),
    signature: z.string(),
    userHandle: z.string().optional(),
});
/**
 * Batched passkey approval (docs/e2e.md "Fewer passkey ceremonies"): the items one assertion
 * covers. Each verifier recomputes its own item; `@viko/crypto` enforces the domain allowlist.
 */
export const ApprovalBatch = z
    .array(z.strictObject({ domain: z.string().min(1).max(64), digest: z.string().length(43) }))
    .min(2)
    .max(32);
/** Passkey endorsement of a device signing key (lets a daemon learn a new device). */
export const DeviceEndorsement = z.object({
    statement: z.object({
        v: z.literal(1),
        type: z.literal('viko.device-endorsement'),
        deviceKeyId: Id,
        /** EC P-256 public JWK. */
        publicKey: z.looseObject({
            kty: z.literal('EC'),
            crv: z.string(),
            x: z.string(),
            y: z.string(),
        }),
        label: z.string(),
        issuedAt: TimestampMs,
        expiresAt: TimestampMs,
        /**
         * Additive: `'session'` marks a "Just this session" browser key (memory-only, ≤ 12 h, no
         * standing authority). Older verifiers strip it, so the passkey signature fails: fail closed.
         */
        scope: z.literal('session').optional(),
    }),
    assertion: WebAuthnAssertion,
    /** Single-ceremony sign-in: the assertion covers `{login, device-endorsement}`; additive. */
    batch: ApprovalBatch.optional(),
});
export const SignedCommand = z.object({
    envelope: CommandEnvelope,
    /** base64url raw r||s P-256 signature by the device key. */
    signature: z.string().min(1),
    /** Inline payload (validated per kind by `parseCommandPayload`). */
    payload: z.unknown().optional(),
    /** Fresh passkey assertion over `createStepUpChallenge(envelope)`; required for risky kinds. */
    stepUp: WebAuthnAssertion.optional(),
    endorsement: DeviceEndorsement.optional(),
    /** Batched step-up: `stepUp` covers these items, one of which is this envelope's challenge. */
    stepUpBatch: ApprovalBatch.optional(),
});
/** Why a daemon refused a command (mirrors `@viko/crypto` CommandRejectReason, plus protocol-level reasons). */
export const CommandRejectReason = z.enum([
    'malformed',
    'unsupported-version',
    'machine-mismatch',
    'unknown-device',
    'bad-endorsement',
    'revoked',
    'bad-signature',
    'payload-mismatch',
    'not-yet-valid',
    'expired',
    'ttl-too-long',
    'missing-step-up',
    'bad-step-up',
    'replay',
    'unknown-kind',
    'invalid-payload',
]);
//# sourceMappingURL=signing.js.map